import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./api.js";

export type Role = "spotter" | "admin" | null;

interface AuthCtx {
  role: Role;
  loading: boolean;
  login: (password: string) => Promise<Role>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({
  role: null,
  loading: true,
  login: async () => null,
  logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<Role>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ role: Role }>("/api/auth/me")
      .then((r) => setRole(r.role))
      .catch(() => setRole(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Ctx.Provider
      value={{
        role,
        loading,
        login: async (password) => {
          const r = await api.post<{ role: Role }>("/api/auth/login", { password });
          setRole(r.role);
          return r.role;
        },
        logout: async () => {
          await api.post("/api/auth/logout");
          setRole(null);
        },
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  return useContext(Ctx);
}
